import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Navigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlgoliaQuickSearch,
  createPersonOnlyHitRenderer,
  createPersonOnlySearchUiState,
  getPersonDisplayName,
  isPersonExactMatch,
  type PersonHit
} from "@/components/AlgoliaSearch";
import Breadcrumb from "@/components/Breadcrumb";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import EmptyState from "@/components/EmptyState";
import IconButton from "@/components/IconButton";
import { ActionCluster } from "@/components/MenuButton";
import PageHeading from "@/components/PageHeading/PageHeading";
import Table from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import usePageTitle from "@/hooks/usePageTitle";
import AppLayout from "@/layouts/AppLayout";
import pluralize from "@/lib/pluralize";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useMembershipServicesNavLinks } from "@/pages/people/_membershipServicesNavLinks";
import {
  PBD_CARD_MANUAL_FULFILLMENT_MAX_PEOPLE,
  PBD_CARD_MANUAL_FULFILLMENT_PATH,
  PBD_CARD_MANUAL_FULFILLMENT_SUBTEXT,
  usePbdCardManualFulfillmentAccess
} from "./access";
import { QUEUE_PBD_CARD_MANUAL_FULFILLMENT_MUTATION } from "./mutations";
import { PBD_SELECTED_PEOPLE_MEMBERSHIP_QUERY } from "./queries";
import { membershipTypeFromHit, membershipTypeFromPerson, type PbdCardMembershipType } from "./_utils";

const personSearchIndexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;
const QUEUE_ERROR_FALLBACK = "Couldn't queue this PBD card request. Try again.";

type SelectedPerson = {
  id: string;
  pgaId: string;
  displayName: string;
  membershipType: PbdCardMembershipType | "";
};

function selectedPersonFromHit(hit: PersonHit): SelectedPerson {
  return {
    id: hit.pga_id,
    pgaId: hit.pga_id,
    displayName: getPersonDisplayName(hit),
    membershipType: membershipTypeFromHit(hit)
  };
}

export default function PbdCardManualFulfillmentPage() {
  usePageTitle(PBD_CARD_MANUAL_FULFILLMENT_PATH, "People | PBD Card Manual Fulfillment");
  const { loaded, canQueue } = usePbdCardManualFulfillmentAccess();
  const navLinks = useMembershipServicesNavLinks();
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const [selectedPeople, setSelectedPeople] = useState<SelectedPerson[]>([]);
  const [searchKey, setSearchKey] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [queueFulfillment, { loading: publishing }] = useMutation(QUEUE_PBD_CARD_MANUAL_FULFILLMENT_MUTATION);

  const selectedPgaIds = useMemo(() => new Set(selectedPeople.map((person) => person.pgaId)), [selectedPeople]);
  const selectedPgaIdList = useMemo(() => selectedPeople.map((person) => person.pgaId), [selectedPeople]);
  const personHitRenderer = useMemo(() => createPersonOnlyHitRenderer(), []);
  const personSearchUiState = useMemo(() => createPersonOnlySearchUiState(personSearchIndexName), []);
  const { data: membershipData } = useQuery(PBD_SELECTED_PEOPLE_MEMBERSHIP_QUERY, {
    skip: selectedPgaIdList.length === 0,
    variables: {
      first: selectedPgaIdList.length,
      input: { pgaIds: selectedPgaIdList }
    }
  });

  const tableRows = useMemo(() => {
    const membershipByPgaId = new Map(
      (membershipData?.people?.nodes || []).map((person) => [person.pgaId, membershipTypeFromPerson(person)])
    );

    return selectedPeople.map((person) => ({
      ...person,
      membershipType: membershipByPgaId.get(person.pgaId) || person.membershipType
    }));
  }, [membershipData, selectedPeople]);

  const removePerson = useCallback((pgaId: string) => {
    setSelectedPeople((current) => current.filter((person) => person.pgaId !== pgaId));
  }, []);

  const columns = useMemo<ColumnDef<SelectedPerson, unknown>[]>(() => {
    const cellClassName = "whitespace-nowrap !h-10 !min-h-0 !py-0";
    const headerClassName = "!py-1 !text-label-medium !font-sans";

    return [
      {
        accessorKey: "displayName",
        header: "Name",
        size: 280,
        enableSorting: false,
        meta: { className: cellClassName, headerClassName: `hide-header-in-column ${headerClassName}` }
      },
      {
        accessorKey: "pgaId",
        header: "PGA ID",
        size: 140,
        enableSorting: false,
        meta: { className: cellClassName, headerClassName }
      },
      {
        accessorKey: "membershipType",
        header: "Type",
        size: 120,
        enableSorting: false,
        cell: ({ getValue }) => renderEmptyCellState(getValue()),
        meta: { className: cellClassName, headerClassName }
      },
      {
        id: "remove",
        header: "",
        size: 40,
        enableSorting: false,
        meta: { className: `${cellClassName} text-right`, headerClassName },
        cell: ({ row }) => (
          <IconButton
            name="close"
            skipMargin
            title={`Remove ${row.original.displayName}`}
            ariaLabel={`Remove ${row.original.displayName}`}
            onClick={() => removePerson(row.original.pgaId)}
            data-testid={`pbd-card-manual-fulfillment-remove-${row.original.pgaId}`}
          />
        )
      }
    ];
  }, [removePerson]);

  useEffect(() => {
    if (searchKey === 0) return;
    requestAnimationFrame(() => {
      const textField =
        searchWrapRef.current?.querySelector<HTMLElement>("md-outlined-text-field") ||
        searchWrapRef.current?.querySelector<HTMLElement>("md-filled-text-field");
      textField?.focus();
    });
  }, [searchKey]);

  if (!loaded) {
    return null;
  }

  if (!canQueue) {
    return <Navigate to="/people/membership-services" replace />;
  }

  const resetSearch = () => {
    setSearchKey((current) => current + 1);
  };

  const addPerson = (hit: PersonHit) => {
    if (!hit.pga_id || selectedPgaIds.has(hit.pga_id)) {
      resetSearch();
      return;
    }
    if (selectedPeople.length >= PBD_CARD_MANUAL_FULFILLMENT_MAX_PEOPLE) {
      showErrorSnackbar(`Cannot add more than ${PBD_CARD_MANUAL_FULFILLMENT_MAX_PEOPLE} people at once`);
      resetSearch();
      return;
    }

    setSelectedPeople((current) => [...current, selectedPersonFromHit(hit)]);
    resetSearch();
  };

  const closeConfirm = () => {
    if (!publishing) setConfirmOpen(false);
  };

  const publish = async () => {
    if (selectedPeople.length === 0 || publishing) return;

    try {
      const result = await queueFulfillment({
        variables: { pgaIds: selectedPeople.map((person) => person.pgaId) }
      });
      const payload = result.data?.queuePbdCardManualFulfillment;
      if (!payload?.success) {
        showErrorSnackbar(payload?.message || QUEUE_ERROR_FALLBACK);
        return;
      }

      showSnackbar(payload.message);
      setSelectedPeople([]);
      setConfirmOpen(false);
    } catch (error) {
      showErrorSnackbar((error as Error).message || QUEUE_ERROR_FALLBACK);
    }
  };

  const selectedCount = selectedPeople.length;
  const selectedPeopleLabel = pluralize(selectedCount, "person", undefined, "people");

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb
        title="Operations"
        compactTitle="PBD Card Manual Fulfillment"
        backLink={{ to: "/people/membership-services" }}
      />
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <PageHeading title="PBD Card Manual Fulfillment" compact />
          <p className="text-body-medium">{PBD_CARD_MANUAL_FULFILLMENT_SUBTEXT}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 md:pt-4">
          <ActionCluster
            actions={[
              {
                text: "Publish",
                onClick: () => setConfirmOpen(true),
                testId: "pbd-card-manual-fulfillment-publish",
                disabled: selectedCount === 0 || publishing,
                loading: publishing
              }
            ]}
          />
        </div>
      </div>
      <div className="flex flex-col gap-6 pt-12">
        <div ref={searchWrapRef}>
          <AlgoliaQuickSearch<PersonHit>
            key={searchKey}
            indexName={personSearchIndexName}
            label="Search People"
            placeholder="Name or PGA ID"
            renderHit={personHitRenderer}
            onSelect={addPerson}
            getDisplayValue={getPersonDisplayName}
            initialUiState={personSearchUiState}
            isExactMatch={isPersonExactMatch}
            filterHits={(hits) => hits.filter((hit) => !selectedPgaIds.has(hit.pga_id))}
            noResultsMessage="No people found"
          />
        </div>
        {selectedCount === 0 ? (
          <EmptyState
            iconName="badge"
            title="No people selected"
            caption="Search for Associates and Members. PBD only gets people who paid for this dues cycle and can get a card."
          />
        ) : (
          <Table<SelectedPerson>
            data={tableRows}
            columns={columns}
            loading={false}
            getRowId={(row) => row.id}
            keyboardNavigation={false}
            renderFooter={() => null}
            data-testid="pbd-card-manual-fulfillment-list"
          />
        )}
      </div>
      <Dialog
        open={confirmOpen}
        headline="Publish PBD card request?"
        onClosed={closeConfirm}
        disableClose={publishing}
        actions={
          <>
            <Button
              variant="text"
              onClick={closeConfirm}
              disabled={publishing}
              testId="pbd-card-manual-fulfillment-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={publish}
              disabled={selectedCount === 0 || publishing}
              loading={publishing}
              testId="pbd-card-manual-fulfillment-confirm"
            >
              Publish
            </Button>
          </>
        }
      >
        <p className="text-body-medium">
          This will queue an ad hoc PBD credential request for {selectedCount} {selectedPeopleLabel}. PBD only gets
          people who paid for this dues cycle and can get a card.
        </p>
      </Dialog>
    </AppLayout>
  );
}
